/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the product page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  const config = window.themeVariables && window.themeVariables.b2bOrderLimits;

  // B2B order value limits only — does not alter Samita Wholesale product MOQ rules.
  // config.enabled is set in js-variables.liquid only for logged-in customers tagged b2b.
  if (!config || !config.enabled) {
    return;
  }

  const MESSAGE_SELECTOR = '.b2b-order-limit-message';
  const CHECKOUT_BUTTON_SELECTOR = 'button.checkout-button, button[name="checkout"]';
  const CART_ROOT_SELECTOR = '#mini-cart, .cart__aside, form.cart__recap, .mini-cart__drawer-footer';
  let eventCartCache = null;

  function getLimitStatus(totalPriceCents) {
    if (typeof totalPriceCents !== 'number' || Number.isNaN(totalPriceCents)) {
      return null;
    }

    if (totalPriceCents < config.minCents) {
      return 'min';
    }

    if (totalPriceCents > config.maxCents) {
      return 'max';
    }

    return null;
  }

  // Parse a single German/English money string to cents. Returns null if unparseable.
  function parseMoneyToCents(text) {
    if (!text) {
      return null;
    }

    const match = String(text)
      .replace(/\u00a0/g, ' ')
      .match(/(\d{1,3}(?:[.\s]\d{3})*,\d{2}|\d+,\d{2}|\d{1,3}(?:,\d{3})*\.\d{2}|\d+\.\d{2}|\d+)/);

    if (!match) {
      return null;
    }

    let raw = match[1].replace(/\s/g, '');
    if (raw.indexOf(',') !== -1 && raw.indexOf('.') !== -1) {
      raw = raw.replace(/\./g, '').replace(',', '.');
    } else if (raw.indexOf(',') !== -1) {
      raw = raw.replace(',', '.');
    } else {
      raw = raw.replace(/,/g, '');
    }

    const euros = parseFloat(raw);
    if (!Number.isFinite(euros)) {
      return null;
    }

    return Math.round(euros * 100);
  }

  function parseAllMoneyToCents(text) {
    const matches = String(text || '')
      .replace(/\u00a0/g, ' ')
      .match(/(\d{1,3}(?:[.\s]\d{3})*,\d{2}|\d+,\d{2}|\d{1,3}(?:,\d{3})*\.\d{2}|\d+\.\d{2})/g);

    if (!matches || !matches.length) {
      return [];
    }

    return matches
      .map((part) => parseMoneyToCents(part))
      .filter((cents) => typeof cents === 'number' && cents > 0);
  }

  /**
   * Convert a Samita/Shopify money value to cents.
   * Samita sometimes exposes final_price already in cents (38000),
   * and sometimes as euros (380). Use retail magnitude to detect which.
   */
  function coerceToCents(value, retailCents) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      return null;
    }

    const asIs = Math.round(value);
    const asEurosToCents = Math.round(value * 100);

    if (typeof retailCents === 'number' && retailCents > 0) {
      const asIsPlausible = asIs <= retailCents && asIs >= retailCents * 0.05;
      const asEurosPlausible = asEurosToCents <= retailCents && asEurosToCents >= retailCents * 0.05;

      // Prefer the interpretation that matches retail's scale.
      if (asIsPlausible && !asEurosPlausible) {
        return asIs;
      }
      if (asEurosPlausible && !asIsPlausible) {
        return asEurosToCents;
      }
      if (asIsPlausible && asEurosPlausible) {
        // Both plausible — prefer closer to a typical wholesale share (~40–70% of retail)
        const mid = retailCents * 0.55;
        return Math.abs(asIs - mid) <= Math.abs(asEurosToCents - mid) ? asIs : asEurosToCents;
      }
      return null;
    }

    // No retail reference: values >= 1000 are almost certainly already cents for this shop.
    return asIs >= 1000 ? asIs : asEurosToCents;
  }

  /**
   * Discounted cart total in cents for B2B min/max.
   * Prefer what the customer sees in Zwischensumme, then Samita final_price.
   */
  function getOrderTotalCents(cart) {
    if (!cart || !cart.item_count) {
      return null;
    }

    const retailCents = typeof cart.total_price === 'number' ? cart.total_price : null;
    const W = window.Samita && window.Samita.Wholesale;

    // 1) Painted Zwischensumme (what the customer sees) — most trustworthy
    const zwischensumme = document.querySelector(
      '#mini-cart .ShippingAndTotal__Line .subtotalss:not(.HighlightText), .cart__recap .subtotalss:not(.HighlightText), [bss-b2b-cart-total-price]'
    );

    if (zwischensumme) {
      const saleEl = zwischensumme.querySelector('.samitaWS-customized-price');
      if (saleEl) {
        const saleCents = parseMoneyToCents(saleEl.textContent);
        if (typeof saleCents === 'number' && saleCents > 0 && (!retailCents || saleCents <= retailCents)) {
          return saleCents;
        }
      }

      const amounts = parseAllMoneyToCents(zwischensumme.textContent);
      if (amounts.length >= 2) {
        const discounted = Math.min.apply(null, amounts);
        if (!retailCents || discounted <= retailCents) {
          return discounted;
        }
      }
    }

    // 2) Samita final_price with euro/cents coercion
    if (W && W.cart && typeof W.cart.final_price === 'number' && W.cart.final_price > 0) {
      const coerced = coerceToCents(W.cart.final_price, retailCents);
      if (typeof coerced === 'number') {
        return coerced;
      }
    }

    // 3) Retail only before Samita has painted
    return retailCents;
  }

  function buildMessageElement(status) {
    const messages = config.messages[status];
    if (!messages) {
      return null;
    }

    const message = document.createElement('div');
    message.className = 'b2b-order-limit-message';
    message.setAttribute('data-b2b-order-limit', status);
    message.setAttribute('role', 'alert');
    message.innerHTML = `<p>${messages.line1}</p><p>${messages.line2}</p>`;
    return message;
  }

  function getCheckoutButtons() {
    return Array.from(document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR));
  }

  // Prefer the visible theme checkout button in the mini-cart / cart footer.
  function getPrimaryCheckoutButton() {
    const buttons = getCheckoutButtons();
    if (!buttons.length) {
      return null;
    }

    const preferred = buttons.find((button) => {
      if (button.classList.contains('samitaWS-clone-checkout-button')) {
        return false;
      }
      const style = window.getComputedStyle(button);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

    return preferred || buttons[0];
  }

  function setCheckoutDisabled(button, disabled) {
    if (!button) {
      return;
    }

    if (disabled) {
      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('checkout-button--disabled');
      button.setAttribute('data-b2b-order-limit-disabled', 'true');
      return;
    }

    // Only clear disable state we applied — never override Samita MOQ disables.
    if (button.getAttribute('data-b2b-order-limit-disabled') === 'true') {
      button.disabled = false;
      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('checkout-button--disabled');
      button.removeAttribute('data-b2b-order-limit-disabled');
    }
  }

  // Always keep the notice directly above the primary checkout button.
  function ensureMessagePlacement(status) {
    const roots = document.querySelectorAll(CART_ROOT_SELECTOR);
    roots.forEach((root) => {
      root.querySelectorAll(MESSAGE_SELECTOR).forEach((el) => el.remove());
    });

    if (!status) {
      return;
    }

    const button = getPrimaryCheckoutButton();
    if (!button || !button.parentElement) {
      return;
    }

    const nextMessage = buildMessageElement(status);
    if (!nextMessage) {
      return;
    }

    button.parentElement.insertBefore(nextMessage, button);
  }

  // Painted discounted Zwischensumme only (ignores Shopify retail).
  function getPaintedDiscountedCents() {
    const zwischensumme = document.querySelector(
      '#mini-cart .ShippingAndTotal__Line .subtotalss:not(.HighlightText), .cart__recap .subtotalss:not(.HighlightText), [bss-b2b-cart-total-price]'
    );
    if (!zwischensumme) {
      return null;
    }

    const saleEl = zwischensumme.querySelector('.samitaWS-customized-price');
    if (saleEl) {
      const saleCents = parseMoneyToCents(saleEl.textContent);
      if (typeof saleCents === 'number' && saleCents > 0) {
        return saleCents;
      }
    }

    const amounts = parseAllMoneyToCents(zwischensumme.textContent);
    if (amounts.length >= 2) {
      return Math.min.apply(null, amounts);
    }

    return null;
  }

  function resolveLimitStatus(cart) {
    if (!cart || !cart.item_count) {
      return null;
    }

    let status = getLimitStatus(getOrderTotalCents(cart));
    const painted = getPaintedDiscountedCents();

    // Never clear the block based on retail if the painted discounted total is still under/over limit.
    if (painted != null) {
      const paintedStatus = getLimitStatus(painted);
      if (paintedStatus) {
        return paintedStatus;
      }
      // Painted discount is within limits — allow even if a stale status lingered.
      return null;
    }

    return status;
  }

  function applyB2bOrderLimits(cart) {
    const resolvedCart = cart || eventCartCache || (window.Samita && window.Samita.Wholesale && window.Samita.Wholesale.cart) || null;
    if (!resolvedCart) {
      return;
    }

    // Keep Shopify item_count if Samita cart object omits it
    if (typeof resolvedCart.item_count !== 'number' && eventCartCache && typeof eventCartCache.item_count === 'number') {
      resolvedCart.item_count = eventCartCache.item_count;
    }

    const status = resolveLimitStatus(resolvedCart);
    const blocked = Boolean(status);

    // Place a single message above the primary checkout button.
    ensureMessagePlacement(status);

    // Disable/enable ALL checkout buttons, including Samita clones (B2B limit only).
    getCheckoutButtons().forEach((button) => {
      setCheckoutDisabled(button, blocked);
    });

    document.querySelectorAll('.cart__recap, .mini-cart__drawer-footer').forEach((el) => {
      if (blocked) {
        el.setAttribute('data-b2b-order-limit-blocked', 'true');
      } else {
        el.removeAttribute('data-b2b-order-limit-blocked');
      }
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
      el.hidden = blocked;
      el.style.display = blocked ? 'none' : '';
    });
  }

  function scheduleApply(cart, delayMs) {
    window.setTimeout(() => {
      applyB2bOrderLimits(cart);
    }, delayMs);
  }

  // Re-check after Samita has had time to paint wholesale subtotals.
  function applyWithSamitaRetry(cart) {
    applyB2bOrderLimits(cart);
    scheduleApply(cart, 400);
    scheduleApply(cart, 1000);
  }

  function isCheckoutSubmission(form, submitter) {
    if (!form) {
      return false;
    }

    if (submitter && submitter.matches && submitter.matches(CHECKOUT_BUTTON_SELECTOR)) {
      return true;
    }

    // Mini-cart uses form="mini-cart-form" on the button outside some structures
    return form.id === 'mini-cart-form' || form.classList.contains('cart__recap');
  }

  function findCheckoutButton(form, submitter) {
    if (submitter && submitter.matches && submitter.matches(CHECKOUT_BUTTON_SELECTOR)) {
      return submitter;
    }

    const inForm = form.querySelector(CHECKOUT_BUTTON_SELECTOR);
    if (inForm) {
      return inForm;
    }

    if (form.id) {
      return document.querySelector(`button[form="${form.id}"].checkout-button, button[form="${form.id}"][name="checkout"]`);
    }

    return null;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const submitter = event.submitter;
      if (!isCheckoutSubmission(form, submitter)) {
        return;
      }

      const checkoutButton = findCheckoutButton(form, submitter);
      const blockedByButton =
        checkoutButton && (checkoutButton.disabled || checkoutButton.classList.contains('checkout-button--disabled'));
      const blockedByMessage = Boolean(
        (checkoutButton && checkoutButton.parentElement && checkoutButton.parentElement.querySelector(MESSAGE_SELECTOR)) ||
          form.querySelector(MESSAGE_SELECTOR) ||
          document.querySelector(`#mini-cart ${MESSAGE_SELECTOR}, .cart__aside ${MESSAGE_SELECTOR}`)
      );

      if (blockedByButton || blockedByMessage) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest(CHECKOUT_BUTTON_SELECTOR);
    if (!button) {
      return;
    }

    const blocked =
      button.disabled ||
      button.classList.contains('checkout-button--disabled') ||
      button.getAttribute('data-b2b-order-limit-disabled') === 'true' ||
      button.closest('[data-b2b-order-limit-blocked="true"]') ||
      Boolean(document.querySelector(`#mini-cart ${MESSAGE_SELECTOR}, .cart__aside ${MESSAGE_SELECTOR}, .cart__recap ${MESSAGE_SELECTOR}`));

    if (blocked) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  document.documentElement.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) {
      eventCartCache = cart;
    }
    applyWithSamitaRetry(cart);
  });

  // Mini-cart section HTML is replaced after cart:refresh; re-apply once DOM settles.
  document.documentElement.addEventListener('cart:refresh', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) {
      eventCartCache = cart;
    }
    const delay = (event.detail && event.detail.replacementDelay) || 0;

    window.setTimeout(() => {
      applyWithSamitaRetry(cart);
    }, delay + 50);
  });

  // Samita fires this after it writes the discounted cart subtotal / final_price.
  document.addEventListener('Samita.Wholesale.cart.updated', () => {
    // Re-fetch Shopify cart so retail cents are accurate for coercion, then apply.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart) {
          eventCartCache = cart;
        }
        applyB2bOrderLimits(cart || eventCartCache);
        scheduleApply(cart || eventCartCache, 200);
      })
      .catch(() => {
        scheduleApply(eventCartCache, 0);
      });
  });

  // Watch Zwischensumme for Samita HTML replacement (~~retail~~ discount).
  let subtotalObserver = null;
  let applyingFromObserver = false;

  function bindSubtotalObserver() {
    if (subtotalObserver) {
      subtotalObserver.disconnect();
      subtotalObserver = null;
    }

    const roots = document.querySelectorAll(
      '#mini-cart .shippingAndTotal, #mini-cart .ShippingAndTotal__Line .subtotalss, .cart__recap .subtotalss, [bss-b2b-cart-total-price]'
    );
    if (!roots.length) {
      return;
    }

    subtotalObserver = new MutationObserver(() => {
      if (applyingFromObserver) {
        return;
      }
      applyingFromObserver = true;
      scheduleApply(eventCartCache, 0);
      window.setTimeout(() => {
        applyingFromObserver = false;
      }, 100);
    });

    roots.forEach((root) => {
      subtotalObserver.observe(root, { childList: true, subtree: true, characterData: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSubtotalObserver);
  } else {
    bindSubtotalObserver();
  }

  // Re-bind after mini-cart section swaps
  document.documentElement.addEventListener('cart:refresh', () => {
    window.setTimeout(bindSubtotalObserver, 100);
  });
})();
